/* eslint-disable sonarjs/no-duplicate-string */
import React, {Fragment} from 'react'

const localeData = {
  en: {
    signInTo: () => 'Login to',
    signIn: () => 'Login',
    signingIn: () => 'Login',
    verifyAndLogin: () => 'Verify and login',
    forgotYourPassword: () => 'Password reset',
    welcome: () => 'Welcome to',
    updateMyPassword: () => 'Update password',
    createNewPassword: () => 'Create new password',
    getNewPassword: () => 'Reset password',
    gettingANewPassword: () => 'Resetting password',
    letsGetStarted: ({hasProductionWarning}) => (hasProductionWarning ? 'I understand, get started' : 'Get started'),
    username: () => 'Username',
    password: () => 'Password',
    confirmPasswordPlaceholder: () => 'Confirm your password',
    renewConfirmPasswordPlaceholder: () => 'Confirm new password',
    confirmPassword: () => 'Confirm password',
    secretCode: () => 'Verification code',
    iForgotMyPassword: () => 'Forgot password',
    iRememberMyPassword: () => 'Back to login',
    usernameIsRequired: () => 'Please enter your username',
    passwordIsRequired: () => 'Please enter your password',
    authFailed: () => (
      <Fragment>
        Your username or password is incorrect. If you forgot your password, please follow the link below. Otherwise, try again or
        {' '}
        <a href='https://support.agillic.com/hc/en-gb' target='_blank' rel='noopener noreferrer'>
          contact support
        </a>
        .
      </Fragment>
    ),
    authWithTwoFactorFailed: () => 'The code is invalid. Please try again or request a new code.',
    secretCodeIsRequired: () => 'Please enter your verification code',
    codeNotArriving: () => 'Didn’t receive a code?',
    sendNewCode: () => 'Send a new code',
    sendingNewSecretCode: () => 'Sending new code',
    codeSendDelayMessage: channel => `We’ve just sent a new verification code to you via ${(channel === 'sms') ? 'SMS' : 'email'}. Please enter the code to login. If you need another code, you’ll be able to request one shortly.`,
    secretCodeSent: channel => `We’ve just sent a verification code to you via ${(channel === 'sms') ? 'SMS' : 'email'}. Please enter the code to login.`,
    newCodeSentMessage: () => 'New code sent!',
    codeSendFailedMessage: type => {
      const localeSendCodeMessage = {
        CODE_SENDING_BLOCKED: 'We’re sorry, we can’t send a new code just yet. Please try again in a few minutes.',
        CODE_SENDING_FAILED: (
          <Fragment>
            Unfortunately, we were unable to send a verification code to you. Please try again or
            {' '}
            <a
              href='https://support.agillic.com/hc/en-gb'
              target='_blank'
              rel='noopener noreferrer'
            >
              contact support
            </a>
            if the problem persists.
          </Fragment>
        ),
        USER_PENALIZED: (
          <Fragment>
            Your access has been blocked due to security reasons. If you need urgent access, please
            {' '}
            <a
              href='https://support.agillic.com/hc/en-gb'
              target='_blank'
              rel='noopener noreferrer'
            >
              contact support
            </a>
            . Otherwise, you can try again in an hour’s time.
          </Fragment>
        )
      }
      return localeSendCodeMessage[type]
    },
    existingPasswordUsed: () => 'You can’t reuse your old password. Please enter a new one.',
    forgotPasswordEmailSent: () => 'We’ve sent you an email with a link to change your password',
    youCanCloseThisPage: () => 'If you haven’t received an email after a few minutes, please check that you’re using the correct username. If your username is correct, please contact your Agillic admin.',
    checkYourEmailTitle: () => 'We’ve sent you an email with a link to change your password',
    passwordsAreNotTheSame: () => 'Your password doesn’t match. Please re-enter the same password.',
    passwordPolicyNotMetLength: minLength => `Please use at least ${minLength} character${(minLength !== 1) ? 's' : ''}`,
    passwordPolicyNotMetLowercase: minLength => `Please use at least ${minLength} lowercase letter${(minLength !== 1) ? 's' : ''}`,
    passwordPolicyNotMetUppercase: minLength => `Please use at least ${minLength} uppercase letter${(minLength !== 1) ? 's' : ''}`,
    passwordPolicyNotMetNumber: minLength => `Please use at least ${minLength} number${(minLength !== 1) ? 's' : ''}`,
    passwordPolicyNotMetSpecial: minLength => `Please use at least ${minLength} special character${(minLength !== 1) ? 's' : ''}`,
    instanceIsDown: () => 'We’re sorry, it looks like your Agillic instance is down. Please try again in a few minutes. If the problem persists, please contact support.',
    contactCustomerService: () => 'Contact Agillic Support',
    updatePasswordFailed: () => 'Password update failed. Please try again.',
    contactAdministrator: () => 'If you’re having trouble logging in, please contact your Agillic admin.',
    ifNoOneHasAccess: () => (
      <Fragment>
        If you’re experiencing company-wide login issues, please
        {' '}
        <a
          href='https://support.agillic.com/hc/en-gb'
          target='_blank'
          rel='noopener noreferrer'
        >
          contact support
        </a>
        .
      </Fragment>
    ),
    prodNewAccountWarning: () => 'You’re creating an account for the Agillic Production database. We recommend that all actions except reporting should be performed in Staging.',
    prodLoginWarning: () => 'Please make sure you understand the implications of editing data in your Production database before you proceed. We recommend that all actions except reporting should be performed in Staging.',
    auditLabel: () => 'State your reason for logging into this customer instance',
    auditInfo: () => 'Your input will be noted for security auditing purposes',
    auditPlaceholder: () => '',
    auditAction: () => 'Continue',
    auditCancel: () => 'Cancel and go back to login',
    auditWarning: () => 'Please explain the reason for logging in',
    goToLogin: () => 'Go to login',
    goToSsoLogin: () => 'Go to Single Sign On',
    submitAuditFailed: () => 'Login failed due to a system error. Please try again.',
    usernamePlaceholder: () => 'Enter username',
    passwordPlaceholder: () => 'Enter password',
    newPasswordPlaceholder: () => 'Create your password',
    renewPasswordPlaceholder: () => 'Create your new password',
    production: () => 'Production',
    staging: () => 'Staging',
    productionWarningTitle: () => 'You’re about to login to Production',
    productionWarningInfo: () => (
      <Fragment>
        We recommend that all actions except reporting should be performed in Staging.
        <br />
        Read more about the
        {' '}
        <a
          href='https://support.agillic.com/hc/en-gb/articles/360019893311'
          target='_blank'
          rel='noopener noreferrer'
        >
          risks of editing in Production
        </a>
        .
      </Fragment>
    ),
    tfaCodeTitle: () => 'Please enter the verification code',
    forgotPasswordTitle: () => 'Reset password',
    forgotPasswordInfo: ({environment}) => `You’re about to change your password for ${environment[0].toUpperCase() + environment.substring(1)}`,
    saveNewPassword: () => 'Save new password',
    passwordRequirementsTitle: () => 'Password requirements:',
    minLength: ({minLength}) => `At least ${minLength} characters in total`,
    minUpperCase: ({minUpperCase}) => `${minUpperCase} uppercase letter${minUpperCase > 1 ? 's' : ''}`,
    minLowercase: ({minLowercase}) => `${minLowercase} lowercase letter${minLowercase > 1 ? 's' : ''}`,
    minNumbers: ({minNumbers}) => `${minNumbers} number${minNumbers > 1 ? 's' : ''}`,
    minSpecial: ({minSpecial}) => `${minSpecial} special character${minSpecial > 1 ? 's' : ''}`,
    welcomeToAgillic: () => 'Welcome to Agillic',
    renewYourPassword: () => 'Create a new password',
    createYourPassword: () => 'Create your password and you’re good to go!',
    linkExpiredTitle: () => 'We’re sorry, the link has expired',
    linkExpiredInfo: () => 'Not to worry! Please contact your Agillic admin to help you complete your account.',
    forgottenPasswordLinkExpiredTitle: () => 'We’re sorry, the link has expired',
    forgottenPasswordLinkExpiredInfo: () => 'Not to worry! Enter your username and we’ll send you a new link.'
  }
}
export default localeData
